import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

import { getTenant } from "../../utils/tenantManager";

import {
  createContactApi,
  deleteContactApi,
  getContactByIdApi,
  getContactsApi,
  getUnreadContactCountApi,
  markContactAsReadApi,
  updateContactApi,
} from "../../services/website/contactService";

export const useContactViewModel = () => {
  const tenantId = getTenant();

  const [contacts, setContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  const [searchParams] = useSearchParams();

  const [page, setPage] = useState(Number(searchParams.get("page") ?? "1"));

  const [search, setSearch] = useState(searchParams.get("search") ?? "");

  const loadContacts = async () => {
    try {
      setLoading(true);

      const [listRes, unreadRes] = await Promise.all([
        getContactsApi(page, 10, tenantId!, search),
        getUnreadContactCountApi(tenantId!),
      ]);

      console.log("Contacts Response:", listRes);

      const result = listRes.data;

      setContacts(result.data ?? []);
      setTotalCount(result.totalCount ?? 0);

      setUnreadCount(unreadRes.data ?? 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
  }, [page, search]);

  const createContact = async (data: any) => {
    try {
      const res = await createContactApi(data, tenantId!);

      if (res?.data?.success) {
        toast.success(res.data.message);

        await loadContacts();

        return true;
      }

      toast.error(res?.data?.message);

      return false;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Create failed");
      return false;
    }
  };

  const updateContact = async (data: any) => {
    try {
      const res = await updateContactApi(data, tenantId!);

      if (res?.data?.success) {
        toast.success(res.data.message);

        await loadContacts();

        return true;
      }

      toast.error(res?.data?.message);

      return false;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Update failed");
      return false;
    }
  };

  const deleteContact = async (id: string) => {
    try {
      const res = await deleteContactApi(id, tenantId!);

      if (res?.success) {
        toast.success(res.message);

        setContacts((prev) => prev.filter((x) => x.id !== id));
        setTotalCount((prev) => prev - 1);

        return true;
      }

      toast.error(res?.message);

      return false;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed");
      return false;
    }
  };

  const getContactById = async (id: string) => {
    try {
      const res = await getContactByIdApi(id, tenantId!);

      console.log("Contact Details:", res);

      return res.data;
    } catch (err) {
      console.error(err);
      toast.error("Failed to load contact");
      return null;
    }
  };

  const markContactAsRead = async (id: string) => {
    try {
      const res = await markContactAsReadApi(id, tenantId!);

      if (res?.message) {
        setContacts((prev) =>
          prev.map((x) =>
            x.id === id
              ? {
                  ...x,
                  isRead: true,
                }
              : x,
          ),
        );

        setUnreadCount((prev) => Math.max(prev - 1, 0));

        return true;
      }

      toast.error(res?.message || "Failed to mark contact as read");
      return false;
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to mark contact as read",
      );
      return false;
    }
  };

  const getUnreadCount = async () => {
    try {
      const res = await getUnreadContactCountApi(tenantId!);

      const count = res?.data ?? 0;

      setUnreadCount(count);

      return count;
    } catch (err: any) {
      console.error("Failed to get unread contact count:", err);

      toast.error(
        err?.response?.data?.message || "Failed to load unread contact count",
      );

      return 0;
    }
  };

  return {
    getUnreadCount,
    contacts,
    unreadCount,
    loading,
    totalCount,

    page,
    setPage,

    search,
    setSearch,

    loadContacts,

    createContact,
    updateContact,
    deleteContact,
    getContactById,
    markContactAsRead,
  };
};
